import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  Check,
  Refresh,
  ErrorOutline,
  HelpOutline,
  Visibility,
  History as HistoryIcon,
} from "@material-ui/icons";
import { axiosInstance } from "../../Utils/ExpressAPI";

/**
 * Poste de travail du secretariat (section 18 du cahier des charges).
 *
 * Trois colonnes, et l'ordre compte :
 *   1. ce qui reclame une decision  -> la file d'exceptions
 *   2. le dossier en cours          -> document a gauche, donnees a droite
 *   3. ce qu'on peut en faire       -> valider, avancer, tracer
 *
 * Principe directeur, repris de la section 19 : on ne demande pas au
 * secretariat de tout controler. Un dossier propre n'apparait pas ici, il suit
 * son chemin. Cet ecran ne montre que ce qui cloche.
 */

// Deux etats a l'ecran, pas quatre (retour du 28/08 : « restons simple »).
// Les quatre niveaux restent en base, ils servent au tri de la file et a la
// mesure de la qualite du moteur ; ils ne s'affichent plus.
const CONFORMITE = {
  conforme: { fond: "#ECFDF5", texte: "#065F46", libelle: "Conforme" },
  non_conforme: { fond: "#FEF2F2", texte: "#991B1B", libelle: "À vérifier" },
};

const useStyles = makeStyles(() => ({
  page: {
    display: "grid",
    // La colonne centrale est la plus large : c'est la qu'on travaille.
    gridTemplateColumns: "320px minmax(0, 1fr) 300px",
    gap: 16,
    padding: 16,
    height: "calc(100vh - 90px)",
    boxSizing: "border-box",
  },
  colonne: { display: "flex", flexDirection: "column", overflow: "hidden" },
  enTete: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: "1px solid #E5E7EB",
  },
  titreColonne: { fontSize: 14, fontWeight: 600, color: "#111827" },
  corps: { overflowY: "auto", padding: 12, flex: 1 },
  carte: {
    padding: 12,
    marginBottom: 8,
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    cursor: "pointer",
  },
  carteActive: { borderColor: "#2563EB", background: "#EFF6FF" },
  motif: { fontSize: 13, fontWeight: 600 },
  meta: { fontSize: 12, color: "#6B7280", marginTop: 2 },
  vide: { padding: 32, textAlign: "center", color: "#6B7280", fontSize: 14 },
  champ: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid #F3F4F6",
  },
  nomChamp: { width: 140, fontSize: 13, color: "#374151", paddingTop: 8 },
  brut: { fontSize: 12, color: "#9CA3AF", marginTop: 2, fontStyle: "italic" },
  pastille: {
    fontSize: 11,
    height: 20,
    fontWeight: 600,
  },
  document: {
    background: "#F9FAFB",
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  action: { marginBottom: 8, textTransform: "none" },
  evenement: { fontSize: 12, color: "#4B5563", padding: "6px 0", borderBottom: "1px solid #F3F4F6" },
  alerte: {
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 6,
    padding: "12px 14px",
    marginBottom: 12,
  },
  alerteTitre: { fontSize: 13, fontWeight: 700, color: "#991B1B" },
  alerteTexte: { fontSize: 13, color: "#7F1D1D", marginTop: 4 },
}));

/** Les dates arrivent en Timestamp Firestore, en ISO ou deja en Date. */
function enDate(valeur) {
  if (!valeur) return null;
  if (valeur._seconds) return new Date(valeur._seconds * 1000);
  if (valeur.seconds) return new Date(valeur.seconds * 1000);
  const d = new Date(valeur);
  return Number.isNaN(d.getTime()) ? null : d;
}

function depuis(valeur) {
  const d = enDate(valeur);
  if (!d) return "";
  const minutes = Math.round((Date.now() - d.getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const heures = Math.round(minutes / 60);
  if (heures < 24) return `il y a ${heures} h`;
  return d.toLocaleDateString("fr-FR");
}

// Le jeton est pose par l'intercepteur de axiosInstance (Utils/ExpressAPI.js).
const entetes = () => ({});

export default function DossiersWorkbench() {
  const classes = useStyles();
  const labId = localStorage.getItem("currentLab");

  const [file, setFile] = useState([]);
  const [total, setTotal] = useState(0);
  const [chargementFile, setChargementFile] = useState(true);
  const [erreur, setErreur] = useState(null);

  const [selection, setSelection] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [chargementDossier, setChargementDossier] = useState(false);
  const [brouillon, setBrouillon] = useState({});
  const [enregistrement, setEnregistrement] = useState(null);
  const [message, setMessage] = useState(null);

  const chargerFile = useCallback(async () => {
    setChargementFile(true);
    setErreur(null);
    try {
      const { data } = await axiosInstance.get("dossiers/file", {
        params: { laboratoryId: labId },
        headers: entetes(),
      });
      setFile(data.dossiers || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErreur(
        e?.response?.data?.error ||
          "La file n'a pas pu être chargée. Le service est peut-être encore en cours de déploiement."
      );
    } finally {
      setChargementFile(false);
    }
  }, [labId]);

  useEffect(() => {
    chargerFile();
  }, [chargerFile]);

  useEffect(() => {
    if (!selection) {
      setDossier(null);
      return;
    }
    let annule = false;
    setChargementDossier(true);
    (async () => {
      try {
        const { data } = await axiosInstance.get(`dossiers/${selection}`, {
          headers: entetes(),
        });
        if (annule) return;
        setDossier(data);
        setBrouillon({});
      } catch (e) {
        if (!annule) setDossier(null);
      } finally {
        if (!annule) setChargementDossier(false);
      }
    })();
    return () => {
      annule = true;
    };
  }, [selection]);

  /** Valide un champ. La proposition du moteur n'est jamais écrasée. */
  const valider = async (extraction) => {
    const valeur =
      brouillon[extraction.id] ??
      extraction.valeurValidee ??
      extraction.valeurNormalisee ??
      extraction.valeurProposee;
    setEnregistrement(extraction.id);
    try {
      await axiosInstance.post(
        `dossiers/extractions/${extraction.id}/valider`,
        { valeurValidee: valeur },
        { headers: entetes() }
      );
      setDossier((d) => ({
        ...d,
        extractions: d.extractions.map((e) =>
          e.id === extraction.id ? { ...e, valeurValidee: valeur, valideLe: new Date() } : e
        ),
      }));
    } catch (e) {
      setErreur(e?.response?.data?.error || "La validation a échoué.");
    } finally {
      setEnregistrement(null);
    }
  };

  /**
   * Met le dossier en file d'envoi. Rien ne part tout de suite : trente
   * secondes d'attente pendant lesquelles l'envoi reste annulable.
   */
  const transmettre = async () => {
    try {
      const { data } = await axiosInstance.post(
        `transmissions-file/${selection}`,
        {},
        { headers: entetes() }
      );
      setMessage(
        `Envoi programmé sous le nom ${data.nomFichier}. Il reste annulable pendant ${Math.round(
          data.delaiAnnulationMs / 1000
        )} secondes depuis la file d'envoi.`
      );
      await chargerFile();
      setSelection(null);
    } catch (e) {
      setErreur(e?.response?.data?.error || "La mise en file a échoué.");
    }
  };

  const avancer = async (statut) => {
    try {
      await axiosInstance.post(
        `dossiers/${selection}/statut`,
        { statut },
        { headers: entetes() }
      );
      await chargerFile();
      setSelection(null);
    } catch (e) {
      setErreur(e?.response?.data?.error || "Le changement d'état a échoué.");
    }
  };

  const extractions = dossier?.extractions || [];
  const documents = dossier?.documents || [];

  return (
    <div className={classes.page}>
      {/* ---------- colonne 1 : ce qui réclame une décision ---------- */}
      <Paper className={classes.colonne}>
        <div className={classes.enTete}>
          <div>
            <div className={classes.titreColonne}>À traiter</div>
            <div className={classes.meta}>
              {chargementFile
                ? "…"
                : `${file.length} sur ${total} dossier${total > 1 ? "s" : ""}`}
            </div>
          </div>
          <Tooltip title="Rafraîchir">
            <IconButton size="small" onClick={chargerFile}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.corps}>
          {chargementFile && (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!chargementFile && erreur && (
            <div className={classes.vide}>
              <ErrorOutline style={{ color: "#B91C1C" }} />
              <div style={{ marginTop: 8 }}>{erreur}</div>
            </div>
          )}
          {!chargementFile && !erreur && file.length === 0 && (
            <div className={classes.vide}>
              <Check style={{ color: "#15803D", fontSize: 32 }} />
              <div style={{ marginTop: 8 }}>
                Rien à traiter. Les dossiers reconnus sans ambiguïté suivent leur
                chemin sans passer par cet écran.
              </div>
            </div>
          )}
          {file.map((d) => (
            <div
              key={d.id}
              className={`${classes.carte} ${
                selection === d.id ? classes.carteActive : ""
              }`}
              onClick={() => setSelection(d.id)}
            >
              <div className={classes.motif}>{d.libelle}</div>
              <div className={classes.meta}>
                {depuis(d.creeLe)}
                {d.motifs.length > 1 && ` · ${d.motifs.length} points à vérifier`}
              </div>
            </div>
          ))}
        </div>
      </Paper>

      {/* ---------- colonne 2 : le dossier en cours ---------- */}
      <Paper className={classes.colonne}>
        <div className={classes.enTete}>
          <div className={classes.titreColonne}>
            {dossier ? "Dossier en cours" : "Aucun dossier sélectionné"}
          </div>
          {dossier?.exception?.libelle && (
            <Chip
              size="small"
              label={dossier.exception.libelle}
              style={{ background: "#FEF2F2", color: "#991B1B", fontWeight: 600 }}
            />
          )}
        </div>
        <div className={classes.corps}>
          {chargementDossier && (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!chargementDossier && !dossier && (
            <div className={classes.vide}>
              Choisissez un dossier dans la colonne de gauche.
            </div>
          )}

          {dossier && (
            <>
              {/* Le motif seul ne suffit pas : la secretaire doit savoir CE QUI
                  cloche pour pouvoir trancher sans rouvrir tout le dossier. */}
              {dossier.exception?.estUneException && (
                <div className={classes.alerte}>
                  <div className={classes.alerteTitre}>
                    {dossier.exception.libelle}
                  </div>
                  {dossier.dossier.raisonQualification && (
                    <div className={classes.alerteTexte}>
                      {dossier.dossier.raisonQualification}
                    </div>
                  )}
                  {(dossier.messagesVerification || []).map((phrase) => (
                    <div className={classes.alerteTexte} key={phrase}>
                      {phrase}
                    </div>
                  ))}
                  {!(dossier.messagesVerification || []).length &&
                    (dossier.dossier.discordancesIdentite || []).length > 0 && (
                      <div className={classes.alerteTexte}>
                        Traits en désaccord entre les documents :{" "}
                        <strong>
                          {dossier.dossier.discordancesIdentite.join(", ")}
                        </strong>
                      </div>
                    )}
                  {dossier.dossier.qualiteOrdonnance?.message && (
                    <div className={classes.alerteTexte}>
                      {dossier.dossier.qualiteOrdonnance.message}
                    </div>
                  )}
                  {dossier.exception.motifs.length > 1 && (
                    <div className={classes.alerteTexte}>
                      {dossier.exception.motifs.length} points à vérifier sur ce
                      dossier.
                    </div>
                  )}
                </div>
              )}

              {documents.map((doc) => (
                <div key={doc.id} className={classes.document}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{doc.type}</div>
                    <div className={classes.meta}>
                      {doc.nombrePages} page{doc.nombrePages > 1 ? "s" : ""} ·{" "}
                      {doc.origine}
                    </div>
                  </div>
                  {doc.cheminStockage && (
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      style={{ textTransform: "none" }}
                      onClick={() => window.open(doc.cheminStockage, "_blank")}
                    >
                      Ouvrir
                    </Button>
                  )}
                </div>
              ))}

              {extractions.length === 0 && (
                <div className={classes.vide}>
                  Aucune donnée extraite pour ce dossier.
                </div>
              )}

              {extractions.map((e) => {
                const c = CONFORMITE[e.conformite] || CONFORMITE.non_conforme;
                const courante =
                  brouillon[e.id] ??
                  affichable(e.valeurValidee ?? e.valeurNormalisee ?? e.valeurProposee);
                return (
                  <div key={e.id} className={classes.champ}>
                    <div className={classes.nomChamp}>{e.champ}</div>
                    <div style={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={courante}
                        onChange={(ev) =>
                          setBrouillon({ ...brouillon, [e.id]: ev.target.value })
                        }
                      />
                      {/* Ce que le moteur a réellement lu reste visible : c'est
                          ce qui permet de trancher sans rouvrir la photo. */}
                      {e.texteBrut && <div className={classes.brut}>lu : « {e.texteBrut} »</div>}
                    </div>
                    <Box display="flex" alignItems="center" style={{ gap: 8, paddingTop: 4 }}>
                      <Chip
                        size="small"
                        className={classes.pastille}
                        label={c.libelle}
                        style={{ background: c.fond, color: c.texte }}
                      />
                      {e.valeurValidee != null ? (
                        <Check style={{ color: "#15803D" }} fontSize="small" />
                      ) : (
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          style={{ textTransform: "none" }}
                          disabled={enregistrement === e.id}
                          onClick={() => valider(e)}
                        >
                          {enregistrement === e.id ? "…" : "Valider"}
                        </Button>
                      )}
                    </Box>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </Paper>

      {/* ---------- colonne 3 : ce qu'on peut en faire ---------- */}
      <Paper className={classes.colonne}>
        <div className={classes.enTete}>
          <div className={classes.titreColonne}>Actions</div>
        </div>
        <div className={classes.corps}>
          {!dossier && (
            <div className={classes.vide}>
              <HelpOutline style={{ color: "#9CA3AF" }} />
              <div style={{ marginTop: 8 }}>
                Les actions apparaissent une fois un dossier ouvert.
              </div>
            </div>
          )}

          {dossier && (
            <>
              <Typography className={classes.meta} style={{ marginBottom: 12 }}>
                État actuel : <strong>{dossier.dossier.statut}</strong>
              </Typography>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                className={classes.action}
                onClick={() => avancer("controle_secretariat")}
              >
                Contrôle secrétariat fait
              </Button>
              <Button
                fullWidth
                variant="outlined"
                className={classes.action}
                onClick={transmettre}
              >
                Transmettre au SGL
              </Button>
              {message && (
                <Typography className={classes.meta} style={{ marginBottom: 8 }}>
                  {message}
                </Typography>
              )}
              <Button
                fullWidth
                variant="outlined"
                className={classes.action}
                onClick={() => avancer("a_verifier")}
              >
                Renvoyer en vérification
              </Button>

              <Divider style={{ margin: "16px 0" }} />

              <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 8 }}>
                <HistoryIcon fontSize="small" style={{ color: "#6B7280" }} />
                <Typography className={classes.titreColonne}>Historique</Typography>
              </Box>
              {(dossier.journal || []).length === 0 && (
                <div className={classes.meta}>Aucun événement.</div>
              )}
              {(dossier.journal || []).map((ev) => (
                <div key={ev.id} className={classes.evenement}>
                  <strong>{ev.action}</strong>
                  <div className={classes.meta}>
                    {ev.auteur} · {depuis(ev.horodatage)}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </Paper>
    </div>
  );
}

/** Une valeur extraite peut etre un objet ; on ne l'affiche pas en [object Object]. */
function affichable(valeur) {
  if (valeur == null) return "";
  if (typeof valeur === "object") {
    return valeur.libelle || valeur.codeNABM || JSON.stringify(valeur);
  }
  return String(valeur);
}
